import { HStack } from '@terra/ui-styled-system';
import { memo, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type {
  TerraUIMessage,
  TerraUIMessageDataPart,
} from '@terra/shared-dto-chat-types';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type ConversationAttachmentReference,
} from '@terra/shared-dto-chat-types';
import { useAttachmentImagePreviewUrls } from '../attachments/ChatAttachmentPreviewsProvider';
import { ChatAttachmentsPillRow } from '../context-pill/ChatAttachmentsPillRow';

function getConversationAttachments(
  message: TerraUIMessage,
): ConversationAttachmentReference[] {
  return message.parts
    .filter(
      (
        part,
      ): part is TerraUIMessageDataPart & {
        data: {
          type: DataMessageTypeEnum.ConversationAttachments;
          attachments: ConversationAttachmentReference[];
        };
      } =>
        part.type === TerraUIMessageDataPartName &&
        part.data.type === DataMessageTypeEnum.ConversationAttachments,
    )
    .flatMap((part) => part.data.attachments ?? []);
}

export type UserMessageCardAttachmentsProps = {
  message: TerraUIMessage;
};
export const UserMessageCardAttachments = memo(
  function UserMessageCardAttachments({
    message,
  }: UserMessageCardAttachmentsProps) {
    const t = useTranslations('designSystem.chat.fileUpload');
    const imagePreviewUrls = useAttachmentImagePreviewUrls();
    const attachments = useMemo(
      () => getConversationAttachments(message),
      [message],
    );

    if (attachments.length === 0) {
      return null;
    }

    return (
      <HStack mb={2} w="full" minW={0}>
        <ChatAttachmentsPillRow
          removeAriaLabel={t('removeFile')}
          items={attachments.map((attachment) => ({
            id: attachment.id,
            fileName: attachment.fileName,
            fileType: attachment.fileType,
            sizeBytes: attachment.sizeBytes,
            previewUrl: imagePreviewUrls[attachment.id],
          }))}
        />
      </HStack>
    );
  },
);
